// Best-effort swatch color for a color name — either a marketing one
// ("Santorini Black") or a normalized base one ("Black"). We match the first
// known base-color word in it and fall back to a neutral grey. Used by the
// sidebar facet dots and the listing-detail spec swatches.
// First match wins, so append new entries — a name that already matches an
// earlier word keeps its current dot.
const BASE_COLORS = [
    ['black', '#0b0b0d'], ['white', '#f5f5f0'], ['silver', '#c8cbd0'],
    ['gray', '#8a8f9e'], ['grey', '#8a8f9e'], ['red', '#b3261e'],
    ['blue', '#2f4b7c'], ['green', '#2f4a37'], ['brown', '#5a3a29'],
    ['beige', '#d8c9a8'], ['tan', '#c8a97e'], ['gold', '#c9a227'],
    ['bronze', '#8c6b3f'], ['orange', '#d2691e'], ['yellow', '#e3c700'],
    ['purple', '#5b3a68'], ['ivory', '#efe8d8'], ['cream', '#efe8d8'],
    ['ebony', '#1a1a1a'], ['charcoal', '#36393f'], ['champagne', '#e6d3a3'],
    ['burgundy', '#6d1f2b'], ['maroon', '#5c1f26'], ['turquoise', '#30b0a5'],
    ['teal', '#1f6f6b'], ['pink', '#e3a0b5'], ['copper', '#a5643c'],
    ['graphite', '#4a4e57'], ['platinum', '#d6d8dc'],
]
export const guessColorDot = (name) => {
    const n = String(name || '').toLowerCase()
    for (const [word, hex] of BASE_COLORS) {
        if (n.includes(word)) return hex
    }
    return '#c0c4ce'
}

// Price shown on an Installed Options filter row. Returns null (render nothing)
// for a missing/invalid/non-positive price so we never show "$0" for an unpriced
// package. msrp is a server-side integer, so whole dollars drop the cents
// ($1,200); the fractional branch only guards an unexpected decimal ($950.50).
export const formatOptionPrice = (msrp) => {
    const n = Number(msrp)
    if (!Number.isFinite(n) || n <= 0) return null
    return n.toDollarFormat({fixedDecimalScale: n % 1 !== 0})
}

// True when two lists hold the same set of string values (order-insensitive,
// duplicates ignored, empty lists included). Used to tell whether a multi-select
// facet still matches its default set so the "Filters" badge doesn't count it.
export const sameStringSet = (a = [], b = []) => {
    const sa = new Set(a)
    const sb = new Set(b)
    if (sa.size !== sb.size) return false
    for (const v of sa) {
        if (!sb.has(v)) return false
    }
    return true
}

// Availability facet: Visor status codes → the labels the sidebar and the active-
// filter ribbon both show. Shared here so the mapping isn't duplicated between them.
export const AVAILABILITY_LABELS = {stock: 'On Lot', transit: 'In Transit', build: 'Building'}

// Capitalize a facet value for display (new -> New). Shared by the New/Used sidebar
// section and the ribbon.
export const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)

// The sidebar-owned filter fields the active-filter ribbon represents, in display
// order, each with its human category and a label resolver. Year/Make/Model live in
// the search bar and are deliberately absent, so they never appear as ribbon chips.
// The label resolver receives (value, options) — only Installed Options needs the
// options list (to turn a package code into its name); every other facet's stored
// value is already its own label (or a code with a fixed map).
const RIBBON_CHIP_CONFIG = [
    {field: 'availability_statuses',           category: 'Availability',   label: (v) => AVAILABILITY_LABELS[v] || v},
    {field: 'inventory_types',                 category: 'Condition',      label: (v) => titleCase(v)},
    {field: 'seats',                           category: 'Seats',          label: (v) => `${v === '8' ? '8+' : v} Seats`},
    {field: 'versions',                        category: 'Trim',           label: (v) => v},
    {field: 'engines',                         category: 'Engine',         label: (v) => v},
    {field: 'body_types',                      category: 'Body Style',     label: (v) => v},
    {field: 'general_exterior_color_filters',  category: 'Exterior Color', label: (v) => v},
    {field: 'specific_exterior_color_filters', category: 'Exterior Color', label: (v) => v},
    {field: 'general_interior_color_filters',  category: 'Interior Color', label: (v) => v},
    {field: 'specific_interior_color_filters', category: 'Interior Color', label: (v) => v},
    {field: 'options',                         category: 'Option',         label: (v, options) => {
        const pkg = (options?.option_packages || []).find((o) => o.item === v)
        return (pkg && (pkg.name || pkg.item)) || v
    }},
    {field: 'features',                        category: 'Feature',        label: (v) => v},
]

// Field list the ribbon derives chips from and its "Clear all" resets — the single
// source of truth for what the ribbon considers an active filter.
export const RIBBON_FILTER_FIELDS = RIBBON_CHIP_CONFIG.map((c) => c.field)

// The reset payload for every sidebar-owned facet — the app's definition of a
// "subfilter", derived from RIBBON_FILTER_FIELDS so there is only ever one such
// list. Returned as a single object so callers can spread it into ONE setFilters
// call (one coordinated update, one request).
//
// Year is deliberately opt-in: it is a sidebar facet AND a search-bar chip, so a
// search-bar cascade (removing Make/Model) must preserve it, while a full
// search-bar "Clear all" includes it.
export const clearedSubFilters = ({includeYears = false} = {}) => {
    const cleared = RIBBON_FILTER_FIELDS.reduce((acc, f) => ({...acc, [f]: []}), {})
    if (includeYears) cleared.years = []
    return cleared
}

// The filter payload for a programmatic seed: the Quote page's "Find Inventory"
// modal, or a /listings?make=&model=&year= URL. Both start a fresh vehicle-scoped
// search.
//
// The store is an app-lifetime singleton and setFilters MERGES, so a seed naming
// only the vehicle leaves the previous search's sidebar facets in place — a Kia's
// trims, colors, options and features survive onto a Honda and silently narrow it
// to nothing. Every ribbon field is therefore reset in the SAME payload, so one
// seed is still one coordinated update and one request.
//
// The vehicle fields are applied AFTER that reset, so the seed's own New/Used
// preselect wins over the blanket clear. Search context — sort, zip/radius, dealer
// scope, view mode — is deliberately absent: it belongs to the user, not to the
// vehicle, and is what the ribbon's "Clear all" already refuses to reset.
export const seedFiltersForVehicle = ({make, model, year, years, car, inventory_type} = {}) => {
    const searchTags = {}
    if (make) searchTags.make = String(make)
    if (model) searchTags.model = String(model)

    return {
        ...RIBBON_FILTER_FIELDS.reduce((acc, f) => ({...acc, [f]: []}), {}),
        searchTags,
        // Exact-trim BaseCar pk; absent for the name-based seeds (a quote's vehicle).
        car: car ?? null,
        // Year is a multi-select: `years` when the caller already parsed a list (a
        // comma-joined ?year=), else the single year a quote's vehicle carries.
        years: years ?? (year ? [String(year)] : []),
        ...(inventory_type ? {inventory_types: [inventory_type]} : {}),
    }
}

// True when a seed names the vehicle the store already holds. ListingsPage mirrors
// the vehicle into the URL (syncUrlWithSearchTags), so returning to Listings —
// browser Back, or restoring that history entry — remounts with params describing
// the search still in the store. That is a revisit, not a vehicle change, and must
// keep the sidebar facets; only a DIFFERENT vehicle invalidates them.
//
// Deliberately not used by the quote modal: its seed comes from the deal, not from
// the store, so matching the previous search there is a coincidence rather than a
// restore — "Find Inventory" is always an intentional fresh search.
export const isSameVehicleSeed = (seed = {}, filters = {}) => {
    const tags = filters.searchTags || {}
    const norm = (v) => (v == null ? '' : String(v))
    if (norm(seed.make) !== norm(tags.make)) return false
    if (norm(seed.model) !== norm(tags.model)) return false
    // Year is a multi-select, so compare as a set — ?year=2025,2026 and the stored
    // ['2026','2025'] are the same search.
    const seedYears = seed.years ?? (seed.year ? [String(seed.year)] : [])
    return sameStringSet(seedYears, filters.years || [])
}

// The seed fields that survive as URL params — what standaloneListingsUrl writes
// and what ListingsPage reads back on mount. `car` is absent on purpose: a quote's
// vehicle is a name-based seed with no exact-trim pk to carry.
export const SEED_URL_PARAMS = ['make', 'model', 'year', 'zipcode', 'inventory_type']

// The inverse of seedFiltersForVehicle: the same seed as a /listings URL, so the
// quote modal's "Open in new tab" opens the standalone page on the search the
// modal is already showing. Sits next to the seed it mirrors so the params written
// here and the ones ListingsPage reads on mount can't drift apart.
export const standaloneListingsUrl = (seed = {}) => {
    const params = new URLSearchParams()
    for (const key of SEED_URL_PARAMS) {
        const value = seed[key]
        if (value != null && value !== '') params.set(key, String(value))
    }
    const query = params.toString()
    return query ? `/listings?${query}` : '/listings'
}

// The location for a seed. The seed moves the search ORIGIN to the opener's zip
// (a deal's dealer zip, ?zipcode=), but the radius is the user's choice and rides
// along untouched — replacing the whole object would silently drop it back to the
// default. Any lat/lng from an earlier map pan is cleared so the zip is the origin.
export const seedLocation = (currentLocation, zipcode) => ({
    ...currentLocation,
    zipcode,
    latitude: null,
    longitude: null,
})

// Derive the ribbon's chips purely from the current `filters` (+ `options` for the
// option-name lookup). No stored state: one chip per selected value, keyed
// `field:value` so identical labels from different categories stay unique and stable.
// Removal handlers are attached by the ribbon component (they need the store's
// setFilters); this stays a pure, unit-testable projection.
export const activeFilterChips = (filters = {}, options = {}) => {
    const chips = []
    for (const {field, category, label} of RIBBON_CHIP_CONFIG) {
        const values = filters[field] || []
        for (const value of values) {
            chips.push({
                key: `${field}:${value}`,
                field,
                category,
                value,
                label: label(value, options),
            })
        }
    }
    return chips
}

export const groupByDealership = (listings) => {
    const dealershipMap = {};

    for (const listing of listings) {
        const { id, name, latitude, longitude } = listing.dealership;

        if (!dealershipMap[id]) {
            dealershipMap[id] = {
                id,
                name,
                latitude,
                longitude,
                listings: [],
            };
        }

        dealershipMap[id].listings.push(listing);
    }

    return Object.values(dealershipMap);
}